import { defineStore } from 'pinia';
import api from '@/services/api';

// Chave para o estado da listagem no localStorage
const LISTING_STATE_KEY = 'suppliers_listing_state';

/**
 * Suppliers store - manages all supplier-related data and operations
 */
export const useSuppliersStore = defineStore('suppliers', {
  // State
  state: () => ({
    suppliers: [],
    currentSupplier: null,
    isLoading: false,
    error: null,
    filters: {
      search: '',
      category: null,
      status: 'all',
    },
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
    // Estado para importação de CSV
    importState: {
      isImporting: false,
      importError: null,
      importResults: null,
    },
    // Novo estado para armazenar o estado completo da listagem quando navegando para outras páginas
    listingState: null,
  }),

  // Getters
  getters: {
    // Get a specific supplier by ID
    getSupplierById: (state) => (id) => {
      return state.suppliers.find((supplier) => supplier.id === id);
    },

    // Get filtered suppliers list
    filteredSuppliers: (state) => {
      let filtered = [...state.suppliers];

      // Apply search filter
      if (state.filters.search) {
        const searchLower = state.filters.search.toLowerCase();
        filtered = filtered.filter(
          (supplier) =>
            supplier.name.toLowerCase().includes(searchLower) ||
            supplier.email.toLowerCase().includes(searchLower) ||
            supplier.phone.includes(state.filters.search)
        );
      }

      // Apply category filter
      if (state.filters.category) {
        filtered = filtered.filter(
          (supplier) => supplier.categoryId === state.filters.category
        );
      }

      // Apply status filter
      if (state.filters.status !== 'all') {
        filtered = filtered.filter(
          (supplier) => supplier.active === (state.filters.status === 'active')
        );
      }

      return filtered;
    },

    // Get pagination data for current view
    paginatedSuppliers: (state) => {
      const start = (state.pagination.page - 1) * state.pagination.limit;
      const end = start + state.pagination.limit;
      return state.suppliers.slice(start, end);
    },
  },

  // Actions
  actions: {
    // Fetch all suppliers
    async fetchSuppliers() {
      this.isLoading = true;
      try {
        // Here would be the API call
        // Example: const response = await api.get('/suppliers')
        // this.suppliers = await response.data

        // Placeholder implementation
        setTimeout(() => {
          this.suppliers = [
            {
              id: 1,
              name: 'Supplier 1',
              email: 'supplier1@example.com',
              phone: '123456789',
              active: true,
              categoryId: 1,
            },
            {
              id: 2,
              name: 'Supplier 2',
              email: 'supplier2@example.com',
              phone: '987654321',
              active: false,
              categoryId: 2,
            },
          ];
          this.pagination.total = this.suppliers.length;
          this.error = null;
          this.isLoading = false;
        }, 500);
      } catch (err) {
        this.error = err.message || 'Failed to fetch suppliers';
        this.isLoading = false;
      }
    },

    // Add a new supplier
    async addSupplier(supplier) {
      this.isLoading = true;
      try {
        // Here would be the API call
        // Example: const response = await api.post('/suppliers', supplier)
        // const newSupplier = response.data

        // Placeholder implementation
        const newSupplier = {
          id: this.suppliers.length + 1,
          ...supplier,
        };

        this.suppliers.push(newSupplier);
        this.error = null;
        return newSupplier;
      } catch (err) {
        this.error = err.message || 'Failed to add supplier';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Update an existing supplier
    async updateSupplier(id, updates) {
      this.isLoading = true;
      try {
        // Here would be the API call
        // Example: const response = await api.put(`/suppliers/${id}`, updates)
        // const updatedSupplier = response.data

        // Placeholder implementation
        const index = this.suppliers.findIndex((s) => s.id === id);
        if (index > -1) {
          this.suppliers[index] = {
            ...this.suppliers[index],
            ...updates,
          };
          this.error = null;
          return this.suppliers[index];
        } else {
          throw new Error('Supplier not found');
        }
      } catch (err) {
        this.error = err.message || 'Failed to update supplier';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Delete a supplier
    async deleteSupplier(id) {
      this.isLoading = true;
      try {
        // Here would be the API call
        // Example: await api.delete(`/suppliers/${id}`)

        // Placeholder implementation
        const index = this.suppliers.findIndex((s) => s.id === id);
        if (index > -1) {
          this.suppliers.splice(index, 1);
          this.error = null;
          return true;
        } else {
          throw new Error('Supplier not found');
        }
      } catch (err) {
        this.error = err.message || 'Failed to delete supplier';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Set current supplier
    setCurrentSupplier(supplier) {
      this.currentSupplier = supplier;
    },

    // Update filters
    updateFilters(filters) {
      this.filters = {
        ...this.filters,
        ...filters,
      };
    },

    // Update pagination
    updatePagination(pagination) {
      this.pagination = {
        ...this.pagination,
        ...pagination,
      };
    },

    /**
     * Import suppliers from a CSV file
     * @param {File} file - The CSV file to import
     * @returns {Promise<Object>} - The import results
     */
    async importSuppliers(file) {
      this.importState.isImporting = true;
      this.importState.importError = null;
      this.importState.importResults = null;

      try {
        // Create form data for file upload
        const formData = new FormData();
        formData.append('file', file);

        // Make API call to import endpoint
        const response = await api.suppliers.importCsv(formData);

        // Process response
        if (response.data) {
          // Padronizar o formato dos resultados
          const responseData = response.data.data || response.data;

          // Construir um objeto de resultados padronizado
          const results = {
            success:
              responseData.success === true ||
              responseData.status === 'success',
            message: responseData.message || 'Importação realizada',
            total: responseData.total || responseData.totalProcessed || 0,
            totalProcessed:
              responseData.totalProcessed || responseData.total || 0,
            successCount: responseData.successCount || 0,
            errorCount: responseData.errorCount || 0,
            results: responseData.results || [],
          };

          // Se não tiver contagens específicas, mas tiver resultados, calcular
          if (
            results.results.length > 0 &&
            !results.successCount &&
            !results.errorCount
          ) {
            results.successCount = results.results.filter(
              (r) => r.success
            ).length;
            results.errorCount = results.results.filter(
              (r) => !r.success
            ).length;
            results.totalProcessed = results.results.length;
          }

          this.importState.importResults = results;

          // Refresh suppliers list after successful import
          if (results.success) {
            await this.fetchSuppliers({ forceRefresh: true });
          }

          return this.importState.importResults;
        } else {
          throw new Error('Falha ao importar fornecedores: resposta inválida');
        }
      } catch (err) {
        console.error('Erro ao importar fornecedores:', err);
        this.importState.importError =
          err.message || 'Falha ao importar fornecedores';
        throw err;
      } finally {
        this.importState.isImporting = false;
      }
    },

    /**
     * Reset import state
     */
    resetImportState() {
      this.importState = {
        isImporting: false,
        importError: null,
        importResults: null,
      };
    },

    /**
     * Salva o estado atual da listagem (para retorno posterior)
     * @param {Object} state - Estado da listagem a ser salvo
     */
    saveListingState(state) {
      // Salvar no estado da store
      this.listingState = state;

      // Salvar também no localStorage para persistência
      try {
        localStorage.setItem(LISTING_STATE_KEY, JSON.stringify(state));
      } catch (error) {
        console.error(
          'Erro ao salvar o estado da listagem no localStorage:',
          error
        );
      }
    },

    /**
     * Recupera o estado salvo da listagem
     * @returns {Object|null} - O estado da listagem ou null se não existir
     */
    getListingState() {
      // Se já existe no estado da store, retornar diretamente
      if (this.listingState) {
        return this.listingState;
      }

      // Senão, tentar recuperar do localStorage
      try {
        const savedState = localStorage.getItem(LISTING_STATE_KEY);
        if (savedState) {
          this.listingState = JSON.parse(savedState);
          return this.listingState;
        }
      } catch (error) {
        console.error(
          'Erro ao recuperar o estado da listagem do localStorage:',
          error
        );
      }

      return null;
    },

    /**
     * Limpa o estado salvo da listagem
     */
    clearListingState() {
      this.listingState = null;
      try {
        localStorage.removeItem(LISTING_STATE_KEY);
      } catch (error) {
        console.error(
          'Erro ao limpar o estado da listagem do localStorage:',
          error
        );
      }
    },
  },
});
